import { Alert, Button, Container } from '@mui/material';
import { ProductGroups, Routes, ZoomLevels } from 'enums';
// import useStatusMessages from 'features/admin/status-message/use-status-messages';
import ErrorDialog from 'features/ui/app-layout/error-dialog';
import ExpirationDialog from 'features/ui/app-layout/expiration-dialog';
// import _ from 'lodash';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import usePaymentStore from 'stores/payment-store';
import useSidebarStore from 'stores/sidebar-store';
import useUpdateAvailableStore from 'stores/update-available-store';
import useUserStore from 'stores/user-store';
import { getFullLicense } from 'API/license';
import { makeStyles } from 'tss-react/mui';
// import StatusMessageBanner from '../base-components/status-message-banner';
import ErrorBoundary from '../error-boundary/error-boundary';
import LimitReachedDialog from './limit-reached-dialog';
import ContextPanel from './rail/context-panel';
import { CONTEXT_PANEL_WIDTH, getActivePanelItem, getActiveSection, NAV_RAIL_WIDTH, useNavSections } from './rail/nav-config';
import NavRail from './rail/nav-rail';
import TopBar from './topbar/top-bar';
import { ChatDialogHost, ChatFab, ChatPanel } from 'features/ai-chat';
import FeedbackWidget from 'features/feedback/FeedbackWidget';

interface Props {
  children?: React.ReactNode;
}

const AppLayout: React.FC<Props> = ({ children }) => {
  const { t } = useTranslation();
  const { extended, setExtended } = useSidebarStore((state) => ({ extended: state.extended, setExtended: state.setExtended }));
  // const { visibleMessages, handleDismiss } = useStatusMessages(90000); // 90 seconds
  const setActivePricing = usePaymentStore((state) => state.setActivePricing);

  const sections = useNavSections();
  const { signOut, promptOpen, togglePrompt, setLicenses } = useUserStore((state) => ({
    signOut: state.signOut,
    promptOpen: state.promptOpen,
    togglePrompt: state.togglePrompt,
    setLicenses: state.setLicenses,
  }));
  const location = useLocation();
  const navigate = useNavigate();
  const updateAvailable = useUpdateAvailableStore((state) => state.updateAvailable);

  const activeSection = getActiveSection(sections, location.pathname);
  const activeItem = getActivePanelItem(activeSection, location.pathname);
  // The chat page brings its own sidebar; every other section with sub-items gets the context panel.
  const panelAvailable = !!activeSection && activeSection.panelItems.length > 0;
  const panelShown = panelAvailable && extended;
  const offsetLeft = NAV_RAIL_WIDTH + (panelShown ? CONTEXT_PANEL_WIDTH : 0);

  const { classes, cx } = useStyles({ offsetLeft, bannerHeight: 0 });

  const licenseSyncRef = useRef(false);
  useEffect(() => {
    if (licenseSyncRef.current) return;
    licenseSyncRef.current = true;
    (async () => {
      const res = await getFullLicense();
      if (Array.isArray(res)) {
        setLicenses(res);
      }
    })();
  }, []);

  const isHrBooster = (route: string) => {
    return route.includes(Routes.HR_BOOSTER) || route.includes(Routes.RECRUITING_BOOSTER_CO_PILOT);
  };

  const isLeadBooster = (route: string) => {
    return route.includes(Routes.LEAD_BOOSTER) || route.includes(Routes.LEAD_BOOSTER_CO_PILOT);
  };

  useEffect(() => {
    // Rail + context panel: the panel is open by default whenever the active
    // section has one; the topbar menu button collapses it per user choice.
    setExtended(true);

    const lsPricing = localStorage.getItem('active-pricing');

    if (isHrBooster(location.pathname) && lsPricing !== ProductGroups.HR_BOOSTER) {
      setActivePricing(ProductGroups.HR_BOOSTER);
    }
    if (isLeadBooster(location.pathname) && lsPricing !== ProductGroups.LEAD_BOOSTER) {
      setActivePricing(ProductGroups.LEAD_BOOSTER);
    }
  }, [location]);

  const handleCloseTokenExpiredDialog = () => {
    togglePrompt(false);
    signOut();
    // Keep the page they were on: after signing back in they return to their work instead
    // of being dropped at the start.
    const back = encodeURIComponent(`${location.pathname}${location.search}${location.hash}`);
    navigate(`${Routes.PUBLIC_CHAT}?session_expired=true&redirectTo=${back}`);
  };

  const toggleSidebar = () => {
    setExtended(!extended);
  };

  return (
    <div className={classes.wrapper}>
      {/* <StatusMessageBanner visibleMessages={visibleMessages} handleDismiss={handleDismiss} /> */}
      <TopBar offsetLeft={offsetLeft} showPanelToggle={panelAvailable} togglePanel={toggleSidebar} />
      <NavRail />
      {panelShown && activeSection ? <ContextPanel section={activeSection} activeItem={activeItem} /> : null}
      <div
        className={cx(
          classes.appFrame,
          location.pathname === `${location.pathname === Routes.LEAD_LIST_STORAGE}/:id` ? 'show-overflow-x' : null,
          location.pathname.startsWith(Routes.AI_CHAT) ? 'full-bleed' : null
        )}
      >
        {updateAvailable ? (
          <Alert
            severity="info"
            variant="filled"
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1100,
              marginBottom: '16px',
            }}
            action={
              <Button color="inherit" size="small" onClick={() => window.location.reload()}>
                {t('reload')}
              </Button>
            }
          >
            {t('updateAvailable')}
          </Alert>
        ) : null}
        <Container
          className={cx(
            classes.container,
            location.pathname.startsWith(Routes.AI_CHAT) ? 'full-bleed' : null
          )}
        >
          <ErrorBoundary>{children}</ErrorBoundary>
        </Container>
      </div>
      <ExpirationDialog open={promptOpen} handleCloseTokenExpiredDialog={handleCloseTokenExpiredDialog} />
      <LimitReachedDialog />
      <ErrorDialog />
      {/* AI Chat FAB and Panel - hidden on AI Chat page since it has embedded chat */}
      {location.pathname !== Routes.AI_CHAT && (
        <>
          <ChatFab />
          <ChatPanel />
        </>
      )}
      {/*
        Dialoge, die der Chat öffnet (Karte `open_dialog`) — bewusst ohne die Ausnahme
        oben: auf der Chat-Seite gibt es kein Panel, das Fenster muss trotzdem aufgehen.
      */}
      <ChatDialogHost />
      <FeedbackWidget />
    </div>
  );
};

const useStyles = makeStyles<{ offsetLeft: number; bannerHeight: number }>()((theme, { offsetLeft, bannerHeight }) => ({
  wrapper: {
    overflow: 'hidden',

    [theme.breakpoints.down('xl')]: {
      zoom: ZoomLevels.XL,
      height: `calc(100vh / ${ZoomLevels.XL})`,
    },
    [theme.breakpoints.down(1300)]: {
      zoom: ZoomLevels.CUSTOM_LG,
      height: `calc(100vh / ${ZoomLevels.CUSTOM_LG})`,
    },
    [theme.breakpoints.down('lg')]: {
      zoom: ZoomLevels.LG,
      height: `calc(100vh / ${ZoomLevels.LG})`,
    },
    [theme.breakpoints.down(1000)]: {
      zoom: ZoomLevels.CUSTOM_MD,
      height: `calc(100vh / ${ZoomLevels.CUSTOM_MD})`,
    },
    [theme.breakpoints.down('md')]: {
      zoom: ZoomLevels.MD,
      height: `calc(100vh / ${ZoomLevels.MD})`,
    },
  },
  appFrame: {
    height: `calc(100dvh - 60px - ${bannerHeight}px)`,
    marginTop: `calc(60px + ${bannerHeight}px)`,
    // Inhaltsfläche wie im Claude Design (#F6F6FB) statt Weiß
    background: theme.palette.backgrounds.light,
    overflowX: 'hidden',
    padding: `16px 16px 16px ${offsetLeft + 16}px`,
    transition: '225ms',
    '&.show-overflow-x': {
      overflowX: 'auto',
    },
    // AI-Chat: randlos — die Chat-Seite bringt ihr eigenes Layout mit (Sidebar/Panels)
    '&.full-bleed': {
      padding: `0 0 0 ${offsetLeft}px`,
    },

    [theme.breakpoints.down('xl')]: {
      height: `calc((100vh / ${ZoomLevels.XL}) - 60px - ${bannerHeight}px)`,
    },
    [theme.breakpoints.down(1300)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_LG}) - 60px - ${bannerHeight}px)`,
    },
    [theme.breakpoints.down('lg')]: {
      height: `calc((100vh / ${ZoomLevels.LG}) - 60px - ${bannerHeight}px)`,
    },
    [theme.breakpoints.down(1000)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_MD}) - 60px - ${bannerHeight}px)`,
    },
    [theme.breakpoints.down('md')]: {
      height: `calc((100vh / ${ZoomLevels.MD}) - 60px - ${bannerHeight}px)`,
    },
    [theme.breakpoints.up(2300)]: {
      padding: `16px 16px 16px ${offsetLeft + 16}px`,
    },
  },
  logo: {
    width: '24px',
    height: '24px',
  },
  container: {
    maxWidth: 'calc(1920px - 32px)',
    padding: '0px',
    height: '100%',
    '&.full-bleed': {
      maxWidth: 'none !important',
    },

    [theme.breakpoints.down('xl')]: {
      maxWidth: `calc((1920px - 32px) / ${ZoomLevels.XL})`,
    },
    [theme.breakpoints.down(1300)]: {
      maxWidth: `calc((1920px - 32px) / ${ZoomLevels.CUSTOM_LG})`,
    },
    [theme.breakpoints.down('lg')]: {
      maxWidth: `calc((1920px - 32px) / ${ZoomLevels.LG})`,
    },
    [theme.breakpoints.down(1000)]: {
      maxWidth: `calc((1920px - 32px) / ${ZoomLevels.CUSTOM_MD})`,
    },
    [theme.breakpoints.down('md')]: {
      maxWidth: `calc((1920px - 32px) / ${ZoomLevels.MD})`,
    },
  },
}));

export default AppLayout;
